/**
 * DashboardInvoicing — /website-builder/invoices
 *
 * Create / send / void / delete Stripe-backed invoices.
 *   GET    /api/builder/invoices
 *   POST   /api/builder/invoices                  {customer_email, customer_name?, line_items[], notes?, due_days?}
 *   POST   /api/builder/invoices/{id}/send         → finalizes + emails via Stripe
 *   POST   /api/builder/invoices/{id}/void         → voids
 *   POST   /api/builder/invoices/refresh-status    → polls Stripe for paid/void
 *   DELETE /api/builder/invoices/{id}              → drafts only
 *
 * Status badges: draft / sent / paid / void.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText, Plus, Trash2, Send, Loader2, ExternalLink, RefreshCw, X, DollarSign, Ban,
} from 'lucide-react';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-white/8 text-white/65 border-white/15' },
  sent:  { label: 'Sent',  cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25' },
  open:  { label: 'Open',  cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25' },
  paid:  { label: 'Paid',  cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25' },
  void:  { label: 'Void',  cls: 'bg-red-500/15 text-red-300 border-red-400/25' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`text-[9.5px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${m.cls}`} data-testid={`invoice-status-${status}`}>{m.label}</span>;
}

function NewInvoiceModal({ open, onClose, onCreated }) {
  const [email, setEmail]       = useState('');
  const [name, setName]         = useState('');
  const [notes, setNotes]       = useState('');
  const [dueDays, setDueDays]   = useState(30);
  const [items, setItems]       = useState([{ description: '', quantity: 1, unit_amount: '' }]);
  const [busy, setBusy]         = useState(false);

  const reset = () => {
    setEmail(''); setName(''); setNotes(''); setDueDays(30);
    setItems([{ description: '', quantity: 1, unit_amount: '' }]);
  };

  const total = items.reduce((s, li) => s + (parseFloat(li.unit_amount) || 0) * (parseInt(li.quantity) || 0), 0);

  const updateItem = (idx, key, val) => setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const addItem    = () => setItems((arr) => [...arr, { description: '', quantity: 1, unit_amount: '' }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.includes('@')) { toast.error('Valid customer email required'); return; }
    if (items.some((li) => !li.description.trim() || !li.unit_amount)) {
      toast.error('All line items need a description and price'); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/builder/invoices`, {
        customer_email: email,
        customer_name: name,
        line_items: items.map((li) => ({ description: li.description, quantity: parseInt(li.quantity) || 1, unit_amount: parseFloat(li.unit_amount) })),
        notes,
        due_days: parseInt(dueDays) || 30,
      }, { headers: headers() });
      onCreated(r.data);
      toast.success('Draft invoice created');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#15152E] border border-white/10 rounded-2xl w-full max-w-2xl shadow-[0_30px_90px_rgba(0,0,0,0.6)] max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="invoice-new-modal"
      >
        <div className="px-7 py-5 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center">
              <FileText size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold font-syne text-lg">Create invoice</h3>
              <p className="text-[11.5px] text-white/55">Stripe will email a hosted invoice with one-click payment.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5" data-testid="invoice-modal-close"><X size={16} /></button>
        </div>

        <div className="px-7 py-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[#A5A8C9] text-[11px]">Customer email *</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="client@studio.com" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="invoice-modal-email" />
            </div>
            <div>
              <Label className="text-[#A5A8C9] text-[11px]">Customer name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ACME Co." className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="invoice-modal-name" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[#A5A8C9] text-[11px]">Line items *</Label>
              <button type="button" onClick={addItem} className="text-[10px] uppercase tracking-wider font-bold text-violet-300 hover:text-white flex items-center gap-1" data-testid="invoice-modal-add-item">
                <Plus size={11} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_70px_110px_28px] gap-2 items-center" data-testid={`invoice-modal-row-${idx}`}>
                  <Input value={li.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Description" className="bg-white/5 border-white/10 text-white text-sm rounded-xl" data-testid={`invoice-modal-desc-${idx}`} />
                  <Input value={li.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} type="number" min="1" className="bg-white/5 border-white/10 text-white text-sm rounded-xl text-center" data-testid={`invoice-modal-qty-${idx}`} />
                  <div className="relative">
                    <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input value={li.unit_amount} onChange={(e) => updateItem(idx, 'unit_amount', e.target.value)} type="number" step="0.01" min="0" placeholder="0.00" className="bg-white/5 border-white/10 text-white text-sm pl-7 rounded-xl" data-testid={`invoice-modal-price-${idx}`} />
                  </div>
                  <button type="button" onClick={() => removeItem(idx)} disabled={items.length === 1} className="p-1.5 rounded-md text-white/40 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`invoice-modal-remove-${idx}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-[11px] uppercase tracking-wider text-white/55">Total: <span className="text-white font-bold text-base ml-1.5 normal-case tracking-normal">${total.toFixed(2)}</span></p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[#A5A8C9] text-[11px]">Due in (days)</Label>
              <Input value={dueDays} onChange={(e) => setDueDays(e.target.value)} type="number" min="1" max="365" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="invoice-modal-due" />
            </div>
          </div>

          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Notes (shown on invoice)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Thanks for your business! Please pay within 30 days." className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl resize-none" data-testid="invoice-modal-notes" />
          </div>
        </div>

        <div className="px-7 py-4 border-t border-white/[0.06] flex items-center justify-end gap-2 bg-[#11112A]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-white/65 hover:text-white" data-testid="invoice-modal-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-50 flex items-center gap-2" data-testid="invoice-modal-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Save draft
          </button>
        </div>
      </form>
    </div>
  );
}

export default function DashboardInvoicing() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [openNew, setOpenNew]   = useState(false);
  const [refreshing, setRefr]   = useState(false);
  const [busyId, setBusyId]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/invoices`, { headers: headers() });
      setItems(r.data?.items || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendInvoice = async (inv) => {
    if (!(await themedConfirm(`Send invoice to ${inv.customer_email}?\n\nStripe will email a hosted invoice page. This action can't be undone.`))) return;
    setBusyId(inv.id);
    try {
      const r = await axios.post(`${API}/builder/invoices/${inv.id}/send`, {}, { headers: headers() });
      setItems((arr) => arr.map((x) => (x.id === inv.id ? r.data : x)));
      toast.success(`Invoice sent to ${inv.customer_email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusyId(null); }
  };

  const voidInvoice = async (inv) => {
    if (!(await themedConfirm(`Void this invoice?\n\nThe customer's payment page will show "voided".`))) return;
    setBusyId(inv.id);
    try {
      const r = await axios.post(`${API}/builder/invoices/${inv.id}/void`, {}, { headers: headers() });
      setItems((arr) => arr.map((x) => (x.id === inv.id ? r.data : x)));
      toast.success('Invoice voided');
    } catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
    finally { setBusyId(null); }
  };

  const deleteInvoice = async (inv) => {
    if (!(await themedConfirm('Delete this draft invoice?'))) return;
    setBusyId(inv.id);
    try {
      await axios.delete(`${API}/builder/invoices/${inv.id}`, { headers: headers() });
      setItems((arr) => arr.filter((x) => x.id !== inv.id));
      toast.success('Draft deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusyId(null); }
  };

  const refreshStatuses = async () => {
    setRefr(true);
    try {
      const r = await axios.post(`${API}/builder/invoices/refresh-status`, {}, { headers: headers() });
      toast.success(`Checked ${r.data.checked} · ${r.data.updated} updated`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    finally { setRefr(false); }
  };

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Invoicing"
        title="Invoices"
        subtitle="Send invoices powered by Stripe. Customers pay via a hosted page; you get paid into your Stripe balance."
        actions={
          <div className="flex items-center gap-2">
            <SqsButton variant="ghost" size="sm" icon={refreshing ? Loader2 : RefreshCw} onClick={refreshStatuses} disabled={refreshing} data-testid="invoices-refresh">
              {refreshing ? 'Syncing…' : 'Refresh status'}
            </SqsButton>
            <SqsButton variant="primary" size="sm" icon={Plus} onClick={() => setOpenNew(true)} data-testid="invoices-new">New Invoice</SqsButton>
          </div>
        }
      />
      <div className="p-10 max-w-5xl" data-testid="invoices-page">
        {loading ? (
          <div className="flex items-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-[#15152E] border border-white/[0.06] rounded-xl p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#A78BFA]/10 flex items-center justify-center mx-auto mb-4">
              <FileText size={22} className="text-[#A78BFA]" />
            </div>
            <h3 className="text-[16px] font-bold text-white font-syne">No invoices yet</h3>
            <p className="text-[#9CA3AF] text-[12.5px] mt-1 max-w-md mx-auto">Create your first invoice. Once sent, Stripe emails a hosted payment page that accepts cards, Apple/Google Pay, and ACH.</p>
            <button onClick={() => setOpenNew(true)} className="mt-5 px-5 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="invoices-empty-create">Create your first invoice</button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((inv) => {
              const busy = busyId === inv.id;
              return (
                <div key={inv.id} className="bg-[#15152E] border border-white/[0.06] rounded-xl px-5 py-4" data-testid={`invoice-row-${inv.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center shrink-0">
                      <FileText size={16} className="text-[#A78BFA]" />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13.5px] font-semibold text-white truncate">{inv.customer_name || inv.customer_email}</p>
                        <StatusPill status={inv.status} />
                      </div>
                      <p className="text-[11.5px] text-[#9CA3AF] truncate">
                        ${(inv.total ?? 0).toFixed(2)} · {inv.line_items?.length || 0} item{(inv.line_items?.length || 0) === 1 ? '' : 's'} · {inv.customer_email}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {inv.stripe_invoice_url && (
                        <a href={inv.stripe_invoice_url} target="_blank" rel="noreferrer" className="p-2 rounded-md text-white/65 hover:text-white hover:bg-white/5 transition-colors" title="Open hosted invoice" data-testid={`invoice-open-${inv.id}`}>
                          <ExternalLink size={14} />
                        </a>
                      )}
                      {inv.status === 'draft' && (
                        <>
                          <button onClick={() => sendInvoice(inv)} disabled={busy} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_8px_22px_rgba(139,92,246,0.4)] rounded-md flex items-center gap-1.5 disabled:opacity-50" data-testid={`invoice-send-${inv.id}`}>
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
                          </button>
                          <button onClick={() => deleteInvoice(inv)} disabled={busy} className="p-2 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete draft" data-testid={`invoice-delete-${inv.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                      {(inv.status === 'sent' || inv.status === 'open') && (
                        <button onClick={() => voidInvoice(inv)} disabled={busy} className="p-2 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Void invoice" data-testid={`invoice-void-${inv.id}`}>
                          {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NewInvoiceModal open={openNew} onClose={() => setOpenNew(false)} onCreated={(inv) => setItems((arr) => [inv, ...arr])} />
    </>
  );
}
